import React, { useEffect } from "react";
import { Stack, Grid } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import { updateLicense } from "../redux/actions/licenseActions";
import { getProducts } from "../redux/actions/productActions";
import { getTaxCodes } from "../redux/actions/taxCodeActions";
import { cleanFormData } from "../helpers/formHelpers";

const schema = Yup.object({
  productId: Yup.string().required("Product is required"),
  clientInfo: Yup.string().nullable(),
  status: Yup.string().required("Status is required"),
  expiryDate: Yup.date().nullable(),
  renewalPrice: Yup.number().required("Renewal price is required"),
  taxCodeId: Yup.string().required("Tax code is required"),
  autoRenew: Yup.boolean().required(),
  deactivateMachine: Yup.boolean().required(),
});

const UpdateLicenseForm = ({ license, loading }) => {
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.product);
  const { taxCodes } = useSelector((state) => state.taxCode);

  useEffect(() => {
    if (!products || products.length === 0) dispatch(getProducts());
    if (!taxCodes || taxCodes.length === 0) dispatch(getTaxCodes());
  }, [dispatch, products, taxCodes]);

  const productOpts = (products || []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const taxOpts = (taxCodes || []).map((t) => ({
    value: t.id,
    label: `${t.name} (${t.rate}%)`,
  }));

  const statusOpts = [
    { value: "ACTIVE", label: "Active" },
    { value: "EXPIRED", label: "Expired" },
    { value: "REVOKED", label: "Revoked" },
    { value: "PENDING_PAYMENT", label: "Pending Payment" },
  ];
  const boolOpts = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const initValues = {
    productId: license?.productId || "",
    clientInfo: license?.clientInfo || "",
    status: license?.status || "ACTIVE",
    expiryDate: license?.expiryDate
      ? new Date(license.expiryDate).toISOString().slice(0, 16)
      : "",
    renewalPrice: license?.renewalPrice || "",
    taxCodeId: license?.taxCodeId || "",
    autoRenew: license?.autoRenew ?? false,
    deactivateMachine: false,
  };

  return (
    <Formik
      initialValues={initValues}
      validationSchema={schema}
      onSubmit={(values) =>
        dispatch(updateLicense(license.id, cleanFormData(values)))
      }
    >
      {({ isValid, dirty }) => (
        <Form>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="productId"
                  label="Product"
                  options={productOpts}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="taxCodeId"
                  label="Tax Code"
                  options={taxOpts}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="status"
                  label="Status"
                  options={statusOpts}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="renewalPrice"
                  label="Renewal Price"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="autoRenew"
                  label="Auto Renew Enabled?"
                  options={boolOpts}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="deactivateMachine"
                  label="Deactivate Current Machine?"
                  options={boolOpts}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="expiryDate"
                  label="Expiry Date"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  name="clientInfo"
                  label="Client Info"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
            <SubmitButton
              title="Update License"
              loading={loading}
              disabled={!isValid || !dirty || loading}
              startIcon={<SaveOutlinedIcon />}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
export default UpdateLicenseForm;
